// import logo from './logo.svg';
import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import './styles/App.css';
import CreateCorals from './pages/CreateCorals';
import ViewCorals from './pages/ViewCorals';
import CoralDetails from './pages/CoralDetails';
import Dashboard from './pages/Dashboard';
import Reports from './pages/Reports';
import ViewReports from './pages/ViewReports';
import Exports from './pages/Exports';
import CreateExports from './pages/CreateExports';
import RemoveRfidTags from './pages/RemoveRfidTags';
import { brand } from './theme';
import SelectFragmentParent from './pages/SelectFragmentParent';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import ErrorBoundary from './components/ErrorBoundary';
import CustomLogin from './components/CustomLogin';
import awsConfig from './aws-exports';
import { AppBar, Toolbar, Button, Menu, MenuItem, IconButton, Box } from '@mui/material';
import { AccountCircle, ExpandMore } from '@mui/icons-material';
import logo from './logo.png';

Amplify.configure(awsConfig);

const AppContent: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [coralMenuAnchor, setCoralMenuAnchor] = useState<null | HTMLElement>(null);
  const [exportsMenuAnchor, setExportsMenuAnchor] = useState<null | HTMLElement>(null);
  const [reportsMenuAnchor, setReportsMenuAnchor] = useState<null | HTMLElement>(null);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Check for existing user session on app load
  useEffect(() => {
    console.log('App component mounted, checking for user...');
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      console.log('Current user found:', currentUser);
      setUser(currentUser);
    } catch (error) {
      console.log('No authenticated user found:', error);
      // User not signed in
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    console.log('handleSignIn called, checking user status...');
    // Add a small delay to ensure the session is established
    setTimeout(async () => {
      console.log('Delayed checkUser call executing...');
      await checkUser();
    }, 100);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleCoralMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCoralMenuAnchor(event.currentTarget);
  };

  const handleCoralMenuClose = () => {
    setCoralMenuAnchor(null);
  };

  const handleExportsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportsMenuAnchor(event.currentTarget);
  };

  const handleExportsMenuClose = () => {
    setExportsMenuAnchor(null);
  };

  const handleReportsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setReportsMenuAnchor(event.currentTarget);
  };

  const handleReportsMenuClose = () => {
    setReportsMenuAnchor(null);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const isActive = (path: string) => location.pathname === path;
  const isReportsActive = location.pathname.startsWith('/reports');

  if (loading) {
    return (
      <ErrorBoundary>
        <div
          className="App"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <div>Loading...</div>
        </div>
      </ErrorBoundary>
    );
  }

  if (!user) {
    return (
      <ErrorBoundary>
        <div className="App">
          <CustomLogin onSignIn={handleSignIn} />
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="App">
        <AppBar position="static" elevation={2} sx={{ bgcolor: brand.navy, color: '#fff' }}>
          <Toolbar>
            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
              <img src={logo} alt="Monsoon Aquatics" style={{ height: 40, marginRight: 8 }} />
            </Box>

            {/* Navigation Links */}
            <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
              <Button
                component={Link}
                to="/dashboard"
                color="inherit"
                sx={{
                  fontWeight: isActive('/dashboard') ? 'bold' : 'normal',
                  borderBottom: isActive('/dashboard') ? `2px solid ${brand.coral}` : 'none',
                  textDecoration: 'none',
                }}
              >
                Dashboard
              </Button>

              <Button color="inherit" onClick={handleCoralMenuOpen} endIcon={<ExpandMore />}>
                Coral
              </Button>
              <Menu
                anchorEl={coralMenuAnchor}
                open={Boolean(coralMenuAnchor)}
                onClose={handleCoralMenuClose}
              >
                <MenuItem component={Link} to="/coral/create" onClick={handleCoralMenuClose}>
                  Create Corals
                </MenuItem>
                <MenuItem
                  component={Link}
                  to="/coral/fragments/create"
                  onClick={handleCoralMenuClose}
                >
                  Create Fragments
                </MenuItem>
                <MenuItem component={Link} to="/coral/view" onClick={handleCoralMenuClose}>
                  View
                </MenuItem>
                <MenuItem component={Link} to="/coral/tags/remove" onClick={handleCoralMenuClose}>
                  Remove RFID Tags
                </MenuItem>
              </Menu>

              <Button
                color="inherit"
                onClick={handleExportsMenuOpen}
                endIcon={<ExpandMore />}
                sx={{
                  fontWeight: isActive('/exports') ? 'bold' : 'normal',
                  borderBottom: isActive('/exports') ? `2px solid ${brand.coral}` : 'none',
                  textDecoration: 'none',
                }}
              >
                Exports
              </Button>
              <Menu
                anchorEl={exportsMenuAnchor}
                open={Boolean(exportsMenuAnchor)}
                onClose={handleExportsMenuClose}
              >
                <MenuItem component={Link} to="/exports/create" onClick={handleExportsMenuClose}>
                  Create
                </MenuItem>
                <MenuItem component={Link} to="/exports" onClick={handleExportsMenuClose}>
                  View
                </MenuItem>
              </Menu>
              <Button
                color="inherit"
                onClick={handleReportsMenuOpen}
                endIcon={<ExpandMore />}
                sx={{
                  fontWeight: isReportsActive ? 'bold' : 'normal',
                  borderBottom: isReportsActive ? `2px solid ${brand.coral}` : 'none',
                  textDecoration: 'none',
                }}
              >
                Reports
              </Button>
              <Menu
                anchorEl={reportsMenuAnchor}
                open={Boolean(reportsMenuAnchor)}
                onClose={handleReportsMenuClose}
              >
                <MenuItem component={Link} to="/reports/create" onClick={handleReportsMenuClose}>
                  Create
                </MenuItem>
                <MenuItem component={Link} to="/reports/view" onClick={handleReportsMenuClose}>
                  View
                </MenuItem>
              </Menu>
            </Box>

            {/* User Profile */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton color="inherit" onClick={handleProfileMenuOpen} sx={{ ml: 1 }}>
                <AccountCircle />
              </IconButton>
              <Menu
                anchorEl={profileMenuAnchor}
                open={Boolean(profileMenuAnchor)}
                onClose={handleProfileMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => {
                    handleProfileMenuClose();
                    handleSignOut();
                  }}
                >
                  Sign Out
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/coral/create" element={<CreateCorals />} />
          <Route path="/coral/fragments/create" element={<SelectFragmentParent />} />
          <Route path="/coral/view/:parentCoralId/fragments/create" element={<CreateCorals />} />
          <Route path="/coral/view" element={<ViewCorals />} />
          <Route path="/coral/view/:coralId" element={<CoralDetails />} />
          <Route path="/coral/tags/remove" element={<RemoveRfidTags />} />
          <Route path="/exports/create" element={<CreateExports />} />
          <Route path="/exports" element={<Exports />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/reports/create" element={<Reports />} />
          <Route path="/reports/view" element={<ViewReports />} />
          <Route path="/" element={<Dashboard />} />
        </Routes>
      </div>
    </ErrorBoundary>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <Router>
        <AppContent />
      </Router>
    </ErrorBoundary>
  );
};

export default App;
